import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  ColorPicker,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../helpers/api/apiConfig';
import { isMasterUser } from '../helpers/accessControl';
import {
  SIMPLIFIED_RANGE_KEYS,
  collapseRangeMapForSimplifiedEditing,
  expandSimplifiedRangeMapForStorage,
} from '../helpers/metricRules';

const { Title, Text } = Typography;

type WarningRanges = Record<string, { min?: number | null; max?: number | null }>;

interface DiscoveryPayload {
  measurementValueTypes: string[];
  measurementFileTypes: string[];
  satelliteDataTypes: string[];
  weatherFields: string[];
  existingMetricKeys?: string[];
}

interface MetricDefinition {
  id: number;
  key: string;
  name: string;
  sourceType: string;
  sourceKey: string;
  valueTypeKey: string;
  description?: string;
  unit?: string;
  aggregations?: string[];
  defaultLookbackDays?: number | null;
  displayHints?: Record<string, any>;
  colorPalette?: string[] | null;
  warningRanges?: WarningRanges | null;
  active: boolean;
  positiveIsGood?: boolean;
}

const aggregationOptions = [
  { label: 'Avg', value: 'avg' },
  { label: 'Min', value: 'min' },
  { label: 'Max', value: 'max' },
  { label: 'Sum', value: 'sum' },
  { label: 'Count', value: 'count' },
  { label: 'Median', value: 'median' },
  { label: 'Variance', value: 'variance' },
  { label: 'Std Dev', value: 'stddev' },
];

const SIMPLIFIED_WARNING_RANGE_ROWS: Array<{
  key: (typeof SIMPLIFIED_RANGE_KEYS)[number];
  label: string;
  appliesToLabel: string;
}> = [
  {
    key: 'value',
    label: 'Value',
    appliesToLabel: 'Used for value, avg, average, min, max, sum, median',
  },
  {
    key: 'count',
    label: 'Count',
    appliesToLabel: 'Used for count only',
  },
  {
    key: 'variance',
    label: 'Variance / Std Dev',
    appliesToLabel: 'Shared for variance and stddev',
  },
];

const sourceTypeOptions = [
  { label: 'Measurement value', value: 'measurement_value' },
  { label: 'Measurement file', value: 'measurement_file' },
  { label: 'Measurement satellite', value: 'measurement_satellite' },
  { label: 'Measurement weather', value: 'measurement_weather' },
];

const DEFAULT_PALETTE = ['#1677ff', '#2f54eb', '#52c41a', '#faad14', '#ff4d4f'];
const defaultDiscoveries: DiscoveryPayload = {
  measurementValueTypes: [],
  measurementFileTypes: [],
  satelliteDataTypes: [],
  weatherFields: [],
  existingMetricKeys: [],
};

const normalizeColorValue = (value: any): string => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const hexMatch = trimmed.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (hexMatch) return trimmed.toLowerCase();
    const rgbMatch = trimmed.match(/^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i);
    if (rgbMatch) {
      const r = Math.min(255, Number(rgbMatch[1]));
      const g = Math.min(255, Number(rgbMatch[2]));
      const b = Math.min(255, Number(rgbMatch[3]));
      const toHex = (n: number) => n.toString(16).padStart(2, '0');
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    }
    return '';
  }
  if (typeof value?.toHexString === 'function') {
    return value.toHexString();
  }
  return '';
};

const MetricEditor: React.FC = () => {
  const { currentUser } = useAuth();
  const isMaster = isMasterUser(currentUser);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const metricId = params.id ? Number(params.id) : null;
  const isEditMode = Boolean(metricId);
  const isBackOfficeContext = location.pathname.startsWith('/back-office');
  const reportSettingsTarget = isBackOfficeContext
    ? '/back-office?tab=report'
    : '/report-templates/settings';
  const goToMetricsSettings = () => {
    navigate(reportSettingsTarget, { state: { tab: 'metrics' } });
  };

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [discoveries, setDiscoveries] = useState<DiscoveryPayload>(defaultDiscoveries);
  const [loadingDiscovery, setLoadingDiscovery] = useState(false);

  const buildAuthHeaders = (includeContentType = true) => {
    const token = localStorage.getItem('accessToken');
    const csrfToken = localStorage.getItem('csrfToken');
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(includeContentType ? { 'Content-Type': 'application/json' } : {}),
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    };
  };

  const loadMetric = useCallback(async () => {
    if (!metricId) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/metrics/${metricId}`, {
        headers: buildAuthHeaders(),
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        const record: MetricDefinition = data.data || data;
        console.log('[MetricEditor] Loaded metric palette', {
          id: record.id,
          key: record.key,
          colorPalette: record.colorPalette,
        });
        form.setFieldsValue({
          key: record.key,
          name: record.name,
          sourceType: record.sourceType,
          sourceKey: record.sourceKey,
          valueTypeKey: record.valueTypeKey,
          description: record.description,
          unit: record.unit,
          aggregations: record.aggregations || [],
          defaultLookbackDays: record.defaultLookbackDays ?? null,
          active: record.active,
          positiveIsGood: record.positiveIsGood ?? true,
          colorPalette: (() => {
            const incoming = record.colorPalette || [];
            const normalized = incoming.map(normalizeColorValue);
            return new Array(5).fill('').map((_, index) => normalized[index] || DEFAULT_PALETTE[index]);
          })(),
          warningRanges: collapseRangeMapForSimplifiedEditing(record.warningRanges || {}) || {},
        });
      } else {
        const errorData = await response.json();
        message.error(errorData?.message || 'Failed to load metric');
      }
    } catch (error) {
      console.error('Failed to load metric', error);
      message.error('Failed to load metric');
    } finally {
      setLoading(false);
    }
  }, [metricId, form]);

  const fetchDiscoveries = useCallback(async () => {
    setLoadingDiscovery(true);
    try {
      const response = await fetch(`${API_BASE_URL}/metrics/discover?exclude_registered=false`, {
        headers: buildAuthHeaders(false),
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        const payload = data?.data || data || {};
        setDiscoveries({
          measurementValueTypes: payload.measurementValueTypes || [],
          measurementFileTypes: payload.measurementFileTypes || [],
          satelliteDataTypes: payload.satelliteDataTypes || [],
          weatherFields: payload.weatherFields || [],
          existingMetricKeys: payload.existingMetricKeys || [],
        });
      } else {
        const errorData = await response.json();
        message.error(errorData?.message || 'Failed to discover metric sources');
      }
    } catch (error) {
      console.error('Failed to discover metric sources', error);
      message.error('Failed to discover metric sources');
    } finally {
      setLoadingDiscovery(false);
    }
  }, []);

  useEffect(() => {
    if (!isEditMode) {
      form.setFieldsValue({
        active: true,
        colorPalette: DEFAULT_PALETTE,
        aggregations: [],
        warningRanges: {},
        positiveIsGood: true,
      });
      return;
    }
    loadMetric();
  }, [form, isEditMode, loadMetric]);

  useEffect(() => {
    if (isMaster) {
      fetchDiscoveries();
    }
  }, [fetchDiscoveries, isMaster]);

  const normalizeWarningRanges = (ranges: any): WarningRanges | null => {
    if (!ranges || typeof ranges !== 'object') return null;
    const result: WarningRanges = {};
    SIMPLIFIED_RANGE_KEYS.forEach((key) => {
      const raw = (ranges as Record<string, any>)?.[key] as { min?: any; max?: any } | undefined;
      const minValue = raw?.min === '' || raw?.min === undefined ? null : Number(raw.min);
      const maxValue = raw?.max === '' || raw?.max === undefined ? null : Number(raw.max);
      const min = Number.isNaN(minValue as number) ? null : minValue;
      const max = Number.isNaN(maxValue as number) ? null : maxValue;
      if (min !== null || max !== null) {
        result[key] = { min, max };
      }
    });
    return Object.keys(result).length > 0 ? result : null;
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const paletteValues = (values.colorPalette || []).map(normalizeColorValue);
      const paletteFilled = new Array(5).fill('').map((_, index) => paletteValues[index] || DEFAULT_PALETTE[index]);
      const normalizedWarningRanges = normalizeWarningRanges(values.warningRanges);
      const payload = {
        key: values.key,
        name: values.name,
        sourceType: values.sourceType,
        sourceKey: values.sourceKey,
        valueTypeKey: values.valueTypeKey,
        description: values.description,
        unit: values.unit,
        aggregations: values.aggregations || [],
        defaultLookbackDays: values.defaultLookbackDays ?? null,
        active: values.active !== undefined ? Boolean(values.active) : true,
        positiveIsGood:
          values.positiveIsGood !== undefined ? Boolean(values.positiveIsGood) : true,
        colorPalette: paletteFilled,
        warningRanges:
          expandSimplifiedRangeMapForStorage(normalizedWarningRanges || undefined) || null,
      };
      console.log('[MetricEditor] Saving metric', {
        id: metricId,
        isEditMode,
        colorPalette: payload.colorPalette,
      });

      let response: Response;
      if (isEditMode && metricId) {
        response = await fetch(`${API_BASE_URL}/metrics/${metricId}`, {
          method: 'PUT',
          headers: buildAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(`${API_BASE_URL}/metrics`, {
          method: 'POST',
          headers: buildAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify(payload),
        });
      }

      if (response.ok) {
        const responseData = await response.json();
        console.log('[MetricEditor] Save response', {
          id: responseData?.data?.id,
          colorPalette: responseData?.data?.colorPalette,
        });
        message.success(isEditMode ? 'Metric updated' : 'Metric created');
        goToMetricsSettings();
      } else {
        const errorData = await response.json();
        message.error(errorData?.message || 'Failed to save metric');
      }
    } catch (error) {
      console.error('Failed to save metric', error);
      message.error('Failed to save metric');
    } finally {
      setSaving(false);
    }
  };

  const watchedSourceType = Form.useWatch('sourceType', form);
  const watchedSourceKey = Form.useWatch('sourceKey', form);
  const sourceKeyOptions = useMemo(() => {
    if (!watchedSourceType) {
      return [];
    }
    const base =
      watchedSourceType === 'measurement_value'
        ? discoveries.measurementValueTypes
        : watchedSourceType === 'measurement_file'
          ? discoveries.measurementFileTypes
          : watchedSourceType === 'measurement_satellite'
            ? discoveries.satelliteDataTypes
            : watchedSourceType === 'measurement_weather'
              ? discoveries.weatherFields
              : [];
    const unique = new Set((base || []).filter(Boolean));
    if (watchedSourceKey && !unique.has(watchedSourceKey)) {
      unique.add(watchedSourceKey);
    }
    return Array.from(unique).map((value) => ({ label: value, value }));
  }, [discoveries, watchedSourceKey, watchedSourceType]);

  if (!isMaster) {
    return (
      <Alert
        message="Access Restricted"
        description="Only master users can manage metrics."
        type="warning"
        showIcon
      />
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={0} style={{ width: '100%', marginBottom: 16 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={goToMetricsSettings}
          style={{ paddingLeft: 0 }}
        >
          {isBackOfficeContext ? 'Back to Back Office' : 'Back to Report Settings'}
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          {isEditMode ? 'Edit Metric' : 'Create Metric'}
        </Title>
        <Text type="secondary">Define how metrics are calculated and visualized.</Text>
      </Space>

      <Card loading={loading}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Key" name="key" rules={[{ required: true, message: 'Metric key is required' }]}>
                <Input placeholder="e.g. ndvi" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Metric name is required' }]}>
                <Input placeholder="NDVI" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label="Source Type" name="sourceType" rules={[{ required: true }]}>
                <Select options={sourceTypeOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Source Key" name="sourceKey" rules={[{ required: true }]}>
                <Select
                  showSearch
                  allowClear
                  loading={loadingDiscovery}
                  options={sourceKeyOptions}
                  placeholder="Select a source key"
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Value Type Key" name="valueTypeKey" rules={[{ required: true }]}>
                <Input placeholder="measurement value type" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="Optional description" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label="Unit" name="unit">
                <Input placeholder="%" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Default lookback (days)" name="defaultLookbackDays">
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Active" name="active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Higher is better"
                name="positiveIsGood"
                valuePropName="checked"
                extra="Controls whether an upward trend is treated as positive when coloring arrows."
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Aggregations" name="aggregations">
            <Select mode="multiple" options={aggregationOptions} placeholder="Select aggregations" />
          </Form.Item>

          <Card size="small" title="Color palette (5 colors)" style={{ marginBottom: 16 }}>
            <Row gutter={12}>
              {new Array(5).fill(0).map((_, index) => (
                <Col key={`color-${index}`} xs={12} md={4}>
                  <Form.Item
                    name={['colorPalette', index]}
                    label={`Color ${index + 1}`}
                    getValueFromEvent={(value, hex) => normalizeColorValue(hex ?? value)}
                  >
                    <ColorPicker format="hex" showText />
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </Card>

          <Card size="small" title="Warning ranges" style={{ marginBottom: 16 }}>
            <Text type="secondary">
              Set simplified min/max thresholds. Values are applied to related aggregations automatically.
            </Text>
            <div style={{ marginTop: 12 }}>
              {SIMPLIFIED_WARNING_RANGE_ROWS.map((row) => (
                <Row gutter={12} key={row.key} style={{ marginBottom: 8 }}>
                  <Col xs={24} md={6}>
                    <Text strong>{row.label}</Text>
                    <div>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {row.appliesToLabel}
                      </Text>
                    </div>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Item name={['warningRanges', row.key, 'min']} label="Min" style={{ marginBottom: 0 }}>
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Item name={['warningRanges', row.key, 'max']} label="Max" style={{ marginBottom: 0 }}>
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
              ))}
            </div>
          </Card>

          <Space>
            <Button type="primary" onClick={handleSave} loading={saving}>
              {isEditMode ? 'Save changes' : 'Create metric'}
            </Button>
            <Button onClick={goToMetricsSettings}>
              Cancel
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default MetricEditor;
